const DEFAULT_ACTIONS_API_BASE_PATH = '/api/v1';

const ensureLeadingSlash = (path: string) => (path.startsWith('/') ? path : `/${path}`);
const removeTrailingSlash = (path: string) =>
  path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path;

const normalizeBasePath = (rawPath: string, fallback: string) => {
  const trimmed = rawPath.trim();

  if (!trimmed) {
    return fallback;
  }

  return removeTrailingSlash(ensureLeadingSlash(trimmed));
};

const actionsApiBasePath = normalizeBasePath(
  import.meta.env.VITE_ACTIONS_API_BASE_PATH || DEFAULT_ACTIONS_API_BASE_PATH,
  DEFAULT_ACTIONS_API_BASE_PATH,
);

export const API_CONFIG = {
  // Empty string means relative-URL mode: all /api/* calls go to the same origin.
  // In dev Kong is port-forwarded to localhost:3002 and serves as the single entry
  // point — /api calls are routed by Kong to the backend services.
  // In production Kong sits behind the same FQDN as the web app.
  BASE_URL: import.meta.env.VITE_API_BASE_URL || '',
  TIMEOUT: 60000, // 60 seconds
  TOKEN_REFRESH_BUFFER_MS: 5 * 60 * 1000, // 5 minutes before expiry
  CACHE_STALE_TIME_MS: 5 * 60 * 1000, // 5 minutes
  ACTIONS_API_BASE_PATH: actionsApiBasePath,
  ENDPOINTS: {
    ENTITIES: '/api/v1/entities',
    LOCATION_HIERARCHY: '/api/v1/location_hierarchy',
    ENTITY_TYPES: '/api/v1/entity_types',
    HEALTH: '/api/v1/rpc/health',
    RPC: '/api/v1/rpc',
    IEM_CUSTOMER_LOOKUP: `${actionsApiBasePath}/iem/customer-lookup`,
    INDIVIDUAL_CLEAR: `${actionsApiBasePath}/individual/clear`,
    INDIVIDUAL_REACTIVATE: `${actionsApiBasePath}/individual/reactivate`,
    INDIVIDUAL_STATE: `${actionsApiBasePath}/individual/state`,
    IEM_AVAILABILITY_CHECK: `${actionsApiBasePath}/iem/availability-check`,
    IEM_COMPLETE_ISSUE: `${actionsApiBasePath}/iem/complete-issue`,
    IEM_COMPLETE_EXCHANGE: `${actionsApiBasePath}/iem/complete-exchange`,
    IEM_COMPLETE_TURN_IN: `${actionsApiBasePath}/iem/complete-turn-in`,
    IEM_THR_UPDATE: `${actionsApiBasePath}/iem/thr-update`,
    IEM_ALLOCATION_UPDATE: `${actionsApiBasePath}/iem/allocation-update`,
    IEM_ALLOCATION_UPDATE_BATCH: `${actionsApiBasePath}/iem/allocation-update-batch`,
    IEM_IE_KIT_REMOVED_ASSETS: `${actionsApiBasePath}/iem/ie-kit-removed-assets`,
    IEM_IE_KIT_QUANTITY_DECREASED_ASSETS: `${actionsApiBasePath}/iem/ie-kit-quantity-decreased-assets`,
    IEM_IE_KIT_ADDED_ASSETS: `${actionsApiBasePath}/iem/ie-kit-added-assets`,
    IEM_IE_PERSON_ASSIGNMENT_UPDATED: `${actionsApiBasePath}/iem/ie-person-assignment-updated`,
    IEM_AVAILABLE_ASSETS_BY_CATALOG_IDS: `${actionsApiBasePath}/iem/available-assets-by-catalog-ids`,
    IEM_AVAILABLE_ASSET_COUNTS_BY_CATALOG_IDS: `${actionsApiBasePath}/iem/available-asset-counts-by-catalog-ids`,
    IEM_ONHAND_ASSET_COUNTS_BY_CATALOG_IDS: `${actionsApiBasePath}/iem/onhand-asset-counts-by-catalog-ids`,
    IEM_AVAILABILITY_CHECK_BY_CATALOG_IDS: `${actionsApiBasePath}/iem/availability-check-by-catalog-ids`,
    RESET_DEMO_SEED_DATA: `${actionsApiBasePath}/admin/reset-demo-seed-data`,
    BULK_INVENTORY_BIN_COMPLETE: `${actionsApiBasePath}/property-book/bulk-inventory-bin-complete`,
    BULK_INVENTORY_BIN_UPDATE_SERVICE_TYPE: `${actionsApiBasePath}/property-book/bulk-inventory-bin-update-service-type`,
    BULK_INVENTORY_BIN_UPDATE_CONDITION_CODE: `${actionsApiBasePath}/property-book/bulk-inventory-bin-update-condition-code`,
    BULK_DISPOSE_COMPLETE: `${actionsApiBasePath}/property-book/dispose-complete`,
    DISPOSE_RETURN_TO_INVENTORY: `${actionsApiBasePath}/property-book/dispose-return-to-inventory`,
    BULK_DISPOSE_PENDING_TYPE_CHANGE: `${actionsApiBasePath}/bulk-dispose/pending-type-change`,
    BULK_DISPOSE_ADJUST_TYPE: `${actionsApiBasePath}/bulk-dispose/adjust-type`,
    DOCUMENT_RETRIEVAL: `${actionsApiBasePath}/document-retrieval`,
    DOCUMENT_PREVIEW: `${actionsApiBasePath}/document-preview`,
    DOCUMENT_GENERATE: `${actionsApiBasePath}/document-generate`,
    PDF_FORM_FILL: `${actionsApiBasePath}/pdf-form-fill`,
    CUSTOMER_FEEDBACK: `${actionsApiBasePath}/feedback`,
    TRANSACTIONS_LATEST: `${actionsApiBasePath}/transactions/latest`,
    EMAIL_SMTP_CONFIG: `${actionsApiBasePath}/email/admin/smtp-config`,
    EMAIL_OUTBOX: `${actionsApiBasePath}/email/admin/outbox`,
    EMAIL_TEMPLATES: `${actionsApiBasePath}/email/admin/templates`,
    EMAIL_SCHEDULES: `${actionsApiBasePath}/email/schedules`,
    ASSETS_ADJUST_TYPE: `${actionsApiBasePath}/assets/adjust-type`,
    CATALOG_4949_ADJUSTMENT: `${actionsApiBasePath}/catalog/4949-adjustment`,
    CORRECTION_COMPLETE: `${actionsApiBasePath}/transactions/correction-complete`,
    ADMIN_CONFIG: `${actionsApiBasePath}/admin/config`,
    DOCUMENT_STORAGE_CONFIG: `${actionsApiBasePath}/admin/document-storage/config`,
    DOCUMENT_STORAGE_TEST: `${actionsApiBasePath}/admin/document-storage/test`,
    DOCUMENT_STORAGE_MIGRATION_STATUS: `${actionsApiBasePath}/admin/document-storage/migration-status`,
    DOCUMENT_STORAGE_MIGRATE: `${actionsApiBasePath}/admin/document-storage/migrate`,
    SYSTEM_HEALTH: `${actionsApiBasePath}/admin/system-health`,
    SYSTEM_STATUS: `${actionsApiBasePath}/system-status`,
    SYSTEM_LOGS: `${actionsApiBasePath}/admin/system-logs`,
    SYSTEM_LOGS_SUMMARY: `${actionsApiBasePath}/admin/system-logs/summary`,
  },
} as const;
